"use client";

import * as React from "react";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { ChevronsUpDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

/* =========================================================
   Utils
========================================================= */

function debounce<T extends (...args: any[]) => any>(fn: T, delay = 400) {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

/* =========================================================
   Types
========================================================= */

export type CurrencyOption = {
  currency_code: string;
  currency_name: string;
  country_name: string;
  code_alpha2: string;
  useCurrencyFlag?: boolean;
};

function FlagImage({ code, alt }: { code: string; alt: string }) {
  const [visible, setVisible] = React.useState(true);
  if (!visible) return null;

  return (
    <Image
      src={`/flags/${code}.svg`}
      fill 
      className="object-cover"
      alt={alt}
      onError={() => setVisible(false)}
    />
  );
}

/* =========================================================
   Currency Picker — SERVER-PROXY SAFE
========================================================= */

export default function CurrencyPickerCompare({
  value,
  onChange,
}: {
  value: CurrencyOption | null;
  onChange: (c: CurrencyOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [options, setOptions] = useState<CurrencyOption[]>([]);
  const [defaultOptions, setDefaultOptions] = useState<CurrencyOption[]>([]);
  const [groupedOptions, setGroupedOptions] = useState<Record<string, CurrencyOption[]>>({});

  /* ---------- LOAD DEFAULT CURRENCIES (PROXY) ---------- */
  useEffect(() => {
    fetch("/api/home/compare/currencies")
      .then((res) => {
        if (!res.ok) {
          console.error("Failed to fetch currencies:", res.status);
          return { success: false, data: [] };
        }
        return res.json();
      })
      .then((json: any) => {
        // Handle different response structures
        let currencies: any[] = [];

        if (json?.success && json?.data) {
          // Response format: { success: true, data: [...] }
          currencies = Array.isArray(json.data) ? json.data : [];
        } else if (Array.isArray(json)) {
          // Response format: [...] (direct array)
          currencies = json;
        } else if (Array.isArray(json?.data)) {
          // Response format: { data: [...] }
          currencies = json.data;
        }

        const list: CurrencyOption[] = currencies
          .filter((c: any) => c && c.is_receiving_enabled)
          .map((c: any) => ({
            currency_code: c.currency_code,
            currency_name: c.currency_name,
            country_name: c.country_name || c.currency_name,
            code_alpha2: String(c.code_alpha2 || "").toLowerCase(),
            useCurrencyFlag: false,
          }))
          .sort((a: CurrencyOption, b: CurrencyOption) =>
            a.currency_code.localeCompare(b.currency_code),
          );

        setDefaultOptions(list);
        setOptions(list);
      })
      .catch((error) => {
        console.error("Error fetching currencies:", error);
        setDefaultOptions([]);
        setOptions([]);
      });
  }, []);

  /* ---------- SEARCH CURRENCIES (PROXY) ---------- */
  const searchCurrencies = useRef(
    debounce(async (kw: string) => {
      if (!kw) {
        setGroupedOptions({});
        setOptions(defaultOptions);
        return;
      }

      const res = await fetch(
        `/api/home/compare/currencies/search?keyword=${encodeURIComponent(kw)}`,
      );

      if (!res.ok) {
        console.error("Failed to search currencies:", res.status);
        return;
      }

      const json = await res.json();

      // Handle different response structures
      let data: any[] = [];
      if (json?.success && json?.data) {
        data = Array.isArray(json.data) ? json.data : [];
      } else if (Array.isArray(json)) {
        data = json;
      } else if (Array.isArray(json?.data)) {
        data = json.data;
      }

      if (!data || data.length === 0) return;

      const grouped: Record<string, CurrencyOption[]> = {};

      data.forEach((item: any) => {
        if (!item) return;
        const countryName = item.country?.name ?? "Other";
        if (!grouped[countryName]) grouped[countryName] = [];

        if (Array.isArray(item.currencies)) {
          item.currencies.forEach((c: any) => {
            if (!c || !c.is_receiving_enabled) return;

            grouped[countryName].push({
              currency_code: c.currency_code,
              currency_name: c.currency_name,
              country_name: countryName,
              code_alpha2: c.code_alpha2 || c.currency_code.slice(0, 2).toLowerCase(),
              useCurrencyFlag: true,
            });
          });
        }
      });
      setGroupedOptions(grouped);
      setOptions(Object.values(grouped).flat());
    }, 350),
  ).current;

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v && !keyword) setOptions(defaultOptions);
      }}
    >
      <PopoverTrigger asChild>
        <button className="inline-flex items-center justify-between w-full gap-2 px-4 py-3 text-brand-text transition-colors border input-border-radius border-brand-stroke bg-brand-bg">
          <span className="flex items-center gap-2 overflow-hidden">
            {value && (
              <div className="relative w-6 h-6 rounded-full overflow-hidden shrink-0 ring-1 ring-black/[0.06]">
                <FlagImage code={value.code_alpha2} alt={value.currency_code} />
              </div>    
            )}
            <div className="font-medium text-brand-text whitespace-nowrap truncate max-w-[200px]">
              {value?.currency_code ?? "USD"} <span className="max-xs:hidden">- {value?.currency_name}</span>
            </div>
          </span>
          <ChevronsUpDown
            className={`h-4 w-4 opacity-60 transition-all duration-300 ${
              open ? "rotate-180 opacity-100" : ""
            }`}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-[340px] p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search currency or country"
            value={keyword}
            onValueChange={(v) => {
              setKeyword(v);
              if (!v) {
                setOptions(defaultOptions);
                return;
              }
              searchCurrencies(v);
            }}
          />

          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>

            {keyword && Object.keys(groupedOptions).length > 0 ? (
              Object.entries(groupedOptions).map(([country, items]) => (
                <CommandGroup key={country} heading={country}>
                  {items.map((opt) => (
                    <CommandItem
                      key={`${country}-${opt.currency_code}`}
                      onSelect={() => {
                        onChange(opt);
                        setOpen(false);
                      }}
                      className="flex items-center gap-3"
                    >
                      <div className="relative w-6 h-6 rounded-full overflow-hidden shrink-0 ring-1 ring-black/[0.06]">
                        <FlagImage code={opt.code_alpha2} alt={opt.currency_code} />
                      </div>
                      <span className="font-medium">
                        {opt.currency_code} – {opt.currency_name}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))
            ) : (
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={`${opt.currency_code}-${opt.country_name}`}
                    onSelect={() => {
                      onChange(opt);
                      setOpen(false);
                    }}
                    className="flex items-center gap-3"
                  >
                    <div className="relative w-6 h-6 rounded-full overflow-hidden shrink-0 ring-1 ring-black/[0.06]">
                      <FlagImage code={opt.code_alpha2} alt={opt.currency_code} />
                    </div>
                    <span className="font-medium">
                      {opt.currency_code} – {opt.currency_name}
                    </span>
                    {value?.currency_code === opt.currency_code && (
                      <Check className="w-4 h-4 ml-auto" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
